import axios from 'axios';
import {DEFAULT_ERR_MSG, NETWORK_ERR_MSG, getAuthToken} from '../lib/utils';
import userManager from '../auth/userManager';
import {showSessionExpiredModal} from '../lib/sessionExpiredHandler';

// Hardcoded token for legacy API (stageapi.edumarcsms.com)
const LEGACY_API_TOKEN = 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJEYXRhIjp7Imluc3RpdHV0ZUlkIjoiMmI0ZDAyNGYxZjJmNDE4Y2E5MDg3OGMzOWVmMGY0MTAiLCJ0ZW5hbnRfbmFtZSI6IlhZWlplbnRheCBjbGluaWMiLCJyb2xlIjoic3VwcG9ydF9hZ2VudCIsInVzZXJuYW1lIjoic3lzdGVtYWdlbnRfNkBnbWFpbC5jb20iLCJ1c2VySWQiOiIyYjRkMDI0ZjFmMmY0MThjYTkwODc4YzM5ZWYwZjQxMCIsInRlbmFudElkIjoiMmI0ZDAyNGYxZjJmNDE4Y2E5MDg3OGMzOWVmMGY0MTAiLCJwYXJlbnRUZW5hbnRJZCI6IjViMDQ4NjFmMzY2ZWIyMTE1ZGFhZTVmMiIsImFjY291bnRJZCI6IjJiNGQwMjRmMWYyZjQxOGNhOTA4NzhjMzllZjBmNDEwIiwidG9rZW5JZCI6IjFjNDE2ZGYxLWNiOGMtNGU4MS1hMDYzLWY4M2M4ZWM2ODExMCIsInZlcnNpb24iOiJ2MSJ9LCJpc3MiOiJlZHVnby9hdXRoIiwic3ViIjoiMmI0ZDAyNGYxZjJmNDE4Y2E5MDg3OGMzOWVmMGY0MTAiLCJhdWQiOlsiaHR0cHM6Ly9lZHVtYXJjc21zLmNvbSJdLCJleHAiOjE3NjY0NzU0MDMsIm5iZiI6MTc2Mzg4MzQwMywiaWF0IjoxNzYzODgzNDAzLCJqdGkiOiIxYzQxNmRmMS1jYjhjLTRlODEtYTA2My1mODNjOGVjNjgxMTAifQ.LGzIUTqTxyx1LTiaSOXUuPvWn0K4IXvxuPv5Jg4Eb_I';

// Check if URL is for legacy API that needs hardcoded token
const isLegacyApi = (url) => {
  return url && (
    url.includes('stageapi.edumarcsms.com') ||
    url.includes('api.edumarcsms.com') ||
    url.includes('/contact/v1') ||
    url.includes('/sms/v1')
  );
};

// Check if current auth is an impersonation token (not refreshable via OIDC)
const isImpersonating = () => {
  try {
    return !!sessionStorage.getItem('combird_impersonation');
  } catch {
    return false;
  }
};

// Get appropriate token based on URL
const getTokenForUrl = (url) => {
  if (isLegacyApi(url)) {
    return LEGACY_API_TOKEN;
  }
  return getAuthToken();
};

// Cache-bust strategy for the `refreshCachedId` query param.
//
// Older code held a single in-memory counter that only bumped on error
// (for GETs) or on success (for mutations). Result: a pure-read session
// (open page → hit refresh, no writes in between) kept sending the same
// param value forever, so anything caching on the full URL (CDN, server
// response cache, axios adapter cache) returned stale data.
//
// New scheme: param value = current-minute bucket + mutationOffset.
//  - Same minute, no writes → same value → caches hit (intentional, keeps
//    duplicate concurrent reads cheap).
//  - Minute rolls over → value changes → next read busts the cache.
//  - Any mutation increments mutationOffset → next read busts immediately,
//    independent of the minute boundary.
//
// If a specific endpoint needs a longer cache window or no cache at all,
// override `refreshCachedId` in the param object passed to Api.get().
let mutationOffset = 0;
const currentRefreshId = () => Math.floor(Date.now() / 60000) + mutationOffset;

// --- 401 refresh token interceptor ---
let refreshPromise = null;

const triggerSilentRefresh = () => {
  if (!refreshPromise) {
    refreshPromise = userManager.signinSilent()
      .then((user) => user?.access_token || null)
      .catch((e) => {
        console.warn('[auth] silent refresh failed:', e?.message || e);
        return null;
      })
      .finally(() => {
        // Clear after microtask so concurrent callers see the same promise
        setTimeout(() => { refreshPromise = null; }, 0);
      });
  }
  return refreshPromise;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;
    const url = originalRequest?.url;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      isLegacyApi(url) ||
      isImpersonating()
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    const newToken = await triggerSilentRefresh();
    if (!newToken) {
      showSessionExpiredModal();
      return Promise.reject(error);
    }

    const bearer = `Bearer ${newToken}`;
    axios.defaults.headers.common['authorization'] = bearer;
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers['authorization'] = bearer;

    return axios(originalRequest);
  }
);

// --- One-time retry on 502/504 (envoy-gateway cold-burst crashes) ---
// Safe methods only — retrying POST/PUT/PATCH/DELETE risks duplicate
// side-effects when the upstream did receive the request before the
// gateway timed out.
//
// Delay = base + random jitter. The jitter de-synchronises a fan-out of
// concurrent retries (e.g. 6 parallel sign-in calls all 502'ing at once)
// so they don't recreate the same burst that took envoy down.
const GATEWAY_RETRY_STATUSES = new Set([502, 504]);
const GATEWAY_RETRY_METHODS = new Set(['get', 'head', 'options']);
const GATEWAY_RETRY_BASE_MS = 500;
const GATEWAY_RETRY_JITTER_MS = 1000;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;
    const method = (originalRequest?.method || 'get').toLowerCase();

    if (
      !originalRequest ||
      originalRequest._gatewayRetry ||
      !GATEWAY_RETRY_STATUSES.has(status) ||
      !GATEWAY_RETRY_METHODS.has(method)
    ) {
      return Promise.reject(error);
    }

    originalRequest._gatewayRetry = true;
    const delay = GATEWAY_RETRY_BASE_MS + Math.random() * GATEWAY_RETRY_JITTER_MS;
    await new Promise((resolve) => setTimeout(resolve, delay));
    return axios(originalRequest);
  }
);

const handleError = (err) => {
  if (err?.response?.data) {
    err.response.data = {...err.response.data, err: {msg: err.response.data.message || DEFAULT_ERR_MSG}}
  } else if(!err.response) {
    // No response object at all: the request never completed, so nothing about
    // it exists server-side. Name that, and carry the axios code (ERR_NETWORK,
    // ECONNABORTED, ERR_CANCELED, …) into the message — it is the only clue
    // that survives into a support screenshot.
    const code = err?.code ? ` (${err.code})` : ''
    err.response = {data: {err: {msg: `${NETWORK_ERR_MSG}${code}`}}}
  }
}

class Api {
  static headers() {
    let headers = {}
    return headers;
  }

  static get(route, authHeader = true, param = {}) {
    if(authHeader){
      axios.defaults.headers.common['authorization'] = getTokenForUrl(route)
    }
    const params = {
      refreshCachedId: currentRefreshId(), ...param
    }
    return new Promise((resolve, reject) => {
      return axios.get(route,{
        params
      }).then((data)=>{
        resolve(data);
      }).catch((err) => {
        handleError(err)
        reject(err);
      });
    });
  }

  static put(route, params, authHeader = true) {
    if(authHeader){
      axios.defaults.headers.common['authorization'] = getTokenForUrl(route)
    }
    return new Promise((resolve,reject) =>{
      return axios.put(route, params).then((data)=>{
        mutationOffset += 1;
        resolve(data);
      }).catch((err)=>{
        mutationOffset += 1;
        handleError(err)
        reject(err);
      });
    });
  }

  static post(route, params, authHeader = true) {
    if(authHeader){
      axios.defaults.headers.common['authorization'] = getTokenForUrl(route)
    }
    return new Promise((resolve,reject) =>{
      return axios.post(route, params).then((data)=>{
        mutationOffset += 1;
        resolve(data);
      }).catch((err)=>{
        mutationOffset += 1;
        handleError(err)
        reject(err);
      });
    });
  }

  static patch(route, params, authHeader = true) {
    if(authHeader){
      axios.defaults.headers.common['authorization'] = getTokenForUrl(route)
    }
    return new Promise((resolve,reject) =>{
      return axios.patch(route, params).then((data)=>{
        mutationOffset += 1;
        resolve(data);
      }).catch((err)=>{
        mutationOffset += 1;
        handleError(err)
        reject(err);
      });
    });
  }

  static delete(route, authHeader = true) {
    if(authHeader){
      axios.defaults.headers.common['authorization'] = getTokenForUrl(route)
    }
    return new Promise((resolve,reject) =>{
      return axios.delete(route).then((data)=>{
        mutationOffset += 1;
        resolve(data);
      }).catch((err)=>{
        mutationOffset += 1;
        handleError(err)
        reject(err);
      });
    });
  }

  static deleteWithData(route, params, authHeader = true) {
    if(authHeader){
      axios.defaults.headers.common['authorization'] = getTokenForUrl(route)
    }
    return new Promise((resolve,reject) =>{
      return axios.delete(route,{data:params}).then((data)=>{
        mutationOffset += 1;
        resolve(data);
      }).catch((err)=>{
        mutationOffset += 1;
        handleError(err)
        reject(err);
      });
    });
  }

  static xhr(route, params, verb) {

  }
}
export default Api
